'use client';

import { useState, useMemo, useEffect } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { FileText, Download, ExternalLink, FileWarning, Loader2 } from 'lucide-react';
import { resolveFileKind, buildPreviewSrc } from './resume-file-kind';
import { isPrivateResumeUrl } from '@/lib/private-resume';
import { PrivateResumeDrawer } from './private-resume-drawer';

export interface ResumeDrawerProps {
  resumeUrl?: string | null;
  /** Resume/document row id (UUID). Required to build the doc/docx preview URL. */
  resumeId?: string | null;
  /** Server-repaired mime_type — the authoritative format signal. */
  mimeType?: string | null;
  /** Original filename — its extension is the tiebreaker when mime_type is absent. */
  fileName?: string | null;
  candidateName?: string;
  /** Sheet title override (e.g. the document's filename for non-resume docs). */
  title?: string;
  children?: React.ReactNode;
}

type ViewerState = 'loading' | 'ready' | 'error';
// Whether the server-rendered preview object actually exists.
type PreviewProbe = 'checking' | 'ok' | 'missing';

export function ResumeDrawer(props: ResumeDrawerProps) {
  if (isPrivateResumeUrl(props.resumeUrl)) {
    const { resumeUrl, ...drawerProps } = props;
    return <PrivateResumeDrawer {...drawerProps} privateResumeUrl={resumeUrl} />;
  }
  return <LegacyResumeDrawer {...props} />;
}

function LegacyResumeDrawer({ resumeUrl, resumeId, mimeType, fileName, candidateName, title: titleProp, children }: ResumeDrawerProps) {
  const [open, setOpen] = useState(false);
  const [state, setState] = useState<ViewerState>('loading');
  const [probe, setProbe] = useState<PreviewProbe>('checking');

  const kind = useMemo(() => resolveFileKind(mimeType, fileName), [mimeType, fileName]);

  // PDFs: the browser renders the original directly.
  // DOC/DOCX: server-side normalized PDF preview by id — vhire-resume-ingest
  //   renders docx via mammoth and legacy .doc via word-extractor (parse time
  //   + backfill + 15-min self-heal sweep). Built from the same /api/files
  //   origin the file URL already uses.
  // RTF / unknown: no renderer → no inline preview.
  const previewSrc = useMemo(
    () => buildPreviewSrc(resumeUrl, resumeId, kind),
    [resumeUrl, resumeId, kind],
  );

  // A doc/docx server preview at /api/files/preview/{id} may not be generated
  // yet — the resume-worker then returns a plain-text 404 BODY, which an iframe
  // renders as raw text (iframe onError does NOT fire for HTTP errors). So probe
  // with a HEAD first and only mount the iframe when the object really exists;
  // otherwise show the Open/Download fallback. PDFs are the raw file the
  // Open/Download links already point at — no probe needed.
  const needsProbe = !!previewSrc && (kind === 'doc' || kind === 'docx');
  useEffect(() => {
    if (!open || !previewSrc) return;
    if (!needsProbe) {
      setProbe('ok');
      return;
    }
    let cancelled = false;
    setProbe('checking');
    setState('loading');
    const probeUrl = previewSrc.split('#')[0];
    fetch(probeUrl, { method: 'HEAD', redirect: 'follow' })
      .then((r) => { if (!cancelled) setProbe(r.ok ? 'ok' : 'missing'); })
      // Network error (not an HTTP status) — let the iframe try; its own
      // onLoad/onError still applies.
      .catch(() => { if (!cancelled) setProbe('ok'); });
    return () => { cancelled = true; };
  }, [open, previewSrc, needsProbe]);

  if (!resumeUrl) {
    return null;
  }

  // Reset viewer state every time the drawer opens so a previous error doesn't
  // stick when the user reopens.
  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    if (next) {
      setState('loading');
      setProbe(needsProbe ? 'checking' : 'ok');
    }
  };

  const title = titleProp || (candidateName ? `${candidateName}'s Resume` : 'Resume');
  const showIframe = !!previewSrc && probe === 'ok';
  const showPreviewMissing = !!previewSrc && probe === 'missing';

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetTrigger asChild>
        {children || (
          <Button variant="outline" size="sm">
            <FileText className="w-4 h-4 mr-2" />
            Resume
          </Button>
        )}
      </SheetTrigger>
      {/* Measured exception: document preview keeps a 2xl reading pane so PDF
          pages remain legible without forcing the full-screen drawer size. */}
      <SheetContent side="right" className="w-full sm:max-w-2xl p-0 flex flex-col">
        <SheetHeader className="px-6 py-4 border-b shrink-0">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-lg">{title}</SheetTitle>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" asChild>
                <a href={resumeUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="w-4 h-4 mr-1" />
                  Open
                </a>
              </Button>
              <Button variant="outline" size="sm" asChild>
                <a href={resumeUrl} download>
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </a>
              </Button>
            </div>
          </div>
        </SheetHeader>

        <div className="flex-1 relative bg-muted/30 overflow-hidden">
          {/* Probing the server preview, or waiting on the iframe to paint. */}
          {previewSrc && (probe === 'checking' || (showIframe && state === 'loading')) && <PdfSkeleton />}

          {/* iframe render error (network) OR the preview object doesn't exist. */}
          {previewSrc && (state === 'error' || showPreviewMissing) && <PdfError resumeUrl={resumeUrl} />}

          {showIframe && (
            <iframe
              key={previewSrc}
              src={previewSrc!}
              className="w-full h-full border-0"
              title="Resume Preview"
              onLoad={() => setState('ready')}
              onError={() => setState('error')}
              style={{ visibility: state === 'ready' ? 'visible' : 'hidden' }}
            />
          )}

          {!previewSrc && (kind === 'doc' || kind === 'docx') && (
            <UnsupportedFormat
              kind={kind}
              resumeUrl={resumeUrl}
              candidateName={candidateName}
            />
          )}

          {!previewSrc && (kind === 'unknown' || kind === 'rtf') && (
            <UnknownFormat resumeUrl={resumeUrl} />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function PdfSkeleton() {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-muted/30">
      <div className="flex flex-col items-center gap-3 text-muted-foreground">
        <Loader2 className="w-6 h-6 animate-spin" />
        <p className="text-sm">Loading resume…</p>
      </div>
    </div>
  );
}

function PdfError({ resumeUrl }: { resumeUrl: string }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-6">
      <div className="flex flex-col items-center gap-4 max-w-sm text-center">
        <FileWarning className="w-10 h-10 text-muted-foreground" />
        <div>
          <p className="font-medium">Preview unavailable</p>
          <p className="text-sm text-muted-foreground mt-1">
            We couldn&rsquo;t render this resume inline. You can still open or download it.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild>
            <a href={resumeUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-4 h-4 mr-1" />
              Open in new tab
            </a>
          </Button>
          <Button size="sm" asChild>
            <a href={resumeUrl} download>
              <Download className="w-4 h-4 mr-1" />
              Download
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}

function UnsupportedFormat({
  kind,
  resumeUrl,
  candidateName,
}: {
  kind: 'docx' | 'doc';
  resumeUrl: string;
  candidateName?: string;
}) {
  const label = kind === 'docx' ? 'Word document (.docx)' : 'Word document (.doc)';
  return (
    <div className="absolute inset-0 flex items-center justify-center p-6">
      <div className="flex flex-col items-center gap-4 max-w-sm text-center">
        <FileText className="w-10 h-10 text-muted-foreground" />
        <div>
          <p className="font-medium">{label}</p>
          <p className="text-sm text-muted-foreground mt-1">
            No inline preview is available for this file.{' '}
            {candidateName ? `View ${candidateName}'s resume` : 'View it'} in a new tab or
            download it.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild>
            <a href={resumeUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-4 h-4 mr-1" />
              Open in new tab
            </a>
          </Button>
          <Button size="sm" asChild>
            <a href={resumeUrl} download>
              <Download className="w-4 h-4 mr-1" />
              Download
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}

function UnknownFormat({ resumeUrl }: { resumeUrl: string }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center p-6">
      <div className="flex flex-col items-center gap-4 max-w-sm text-center">
        <FileText className="w-10 h-10 text-muted-foreground" />
        <div>
          <p className="font-medium">Preview unavailable</p>
          <p className="text-sm text-muted-foreground mt-1">
            We can&rsquo;t determine the file type. Open or download to view.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" asChild>
            <a href={resumeUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-4 h-4 mr-1" />
              Open
            </a>
          </Button>
          <Button size="sm" asChild>
            <a href={resumeUrl} download>
              <Download className="w-4 h-4 mr-1" />
              Download
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}
